import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import countryList from 'react-select-country-list';

import { Container, Wrapper } from 'components/Layout/Layout';
import { RectangleButton } from 'components/Button/RectangleButton';
import { useActions } from 'hooks/useActions';
import * as actions from './actions';
import Path from 'routes/Path';
import { redirectToCheckout } from 'functions/redirectToCheckout';
import { checkVATForCountries } from 'functions/checkVATForCountries';
import { Spinner } from 'components/Spinner/Spinner';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';
import OptionsTab from './OptionsTab';
import PurchaseSelection from 'components/PurchaseSelection/PurchaseSelection';
import {
  PlansContainer,
  PlansWrapper,
  TabsContainer,
  LabelHeading,
  EmailLink,
  PaymentPageLink,
  VATInput,
  VATStatusText,
} from './styles';

const countriesList = countryList().getData();

export default function PlansPage() {
  const [countries, setCountries] = useState([]);
  const [hasCountry, setHasCountry] = useState(false);
  const [hasVatId, setHasVatId] = useState(false);
  const [vatId, setVatId] = useState('');
  const [vatStatus, setVatStatus] = useState('');
  const [country, setCountry] = useState('');
  const [isPurchaseTab, setIsPurchaseTab] = useState(true);
  const [planDetails, setPlanDetails] = useState({});

  const { loading, plan, payment, updatedCompany, error } = useSelector((state) => state.PlansPageReducer);
  const { company } = useSelector((state) => state.auth);

  const { getPlan, updateCompany, createPayment } = useActions(actions);

  useEffect(() => {
    getPlan();
    setCountries([{ label: 'Select country', value: '' }, ...countriesList]);
  }, [getPlan]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (payment?.id) redirectToCheckout(payment);
  }, [payment]);

  useEffect(() => {
    if (updatedCompany?._id) {
      setHasCountry(true);
      setCountry(updatedCompany.country);
      setHasVatId(true);
      setVatId(updatedCompany.VATInformation.vatId);
      setVatStatus(updatedCompany.VATInformation.verification.status);
    }
  }, [updatedCompany]);

  useEffect(() => {
    if (company?._id) {
      if (company.country) {
        setCountry(company.country);
        setHasCountry(true);
      }
      if (company?.VATInformation?.vatId) {
        setHasVatId(true);
        setVatId(company.VATInformation.vatId);
        setVatStatus(company.VATInformation.verification.status);
      }
    }
  }, [company]);

  const purchaseTest = () => {
    let params = {};
    if (!hasCountry && country) params.country = country;
    if (!hasVatId && vatId) {
      params.VATInformation = {
        vatId: vatId,
      };
      updateCompany(company._id, params);
    } else {
      params.planId = planDetails._id;
      params.currency = planDetails.selectedCurrency;
      params.noOfTests = planDetails.credits;
      params.planType = planDetails.name;
      createPayment(params);
    }
  };

  const isPurchaseButtonDisabled = () => {
    if (!hasCountry && !country) return true;
    if (hasVatId && vatStatus === 'pending') return true;
    return false;
  };

  const renderPurchaseTab = () => (
    <TabsContainer>
      <PurchaseSelection purchaseData={plan} setPurchaseDetails={setPlanDetails} />
      {(!hasCountry || (hasCountry && !hasVatId && checkVATForCountries(country))) && (
        <Dropdown
          width="200px"
          value={country}
          margin="9px 0 0 0"
          justifyContent="center"
          options={countries}
          onChange={(result) => setCountry(result.value)}
        />
      )}
      {country && !hasVatId && checkVATForCountries(country) && (
        <VATInput
          type="text"
          placeholder="VAT ID"
          name="VAT"
          value={vatId}
          onChange={(e) => setVatId(e.target.value)}
        />
      )}
      <span>
        <RectangleButton disabled={isPurchaseButtonDisabled()} onClick={() => purchaseTest()}>
          Purchase
        </RectangleButton>
      </span>
      {vatStatus === 'pending' && (
        <VATStatusText>
          VAT verification is pending. This usually takes 1-2 minutes. <br />
          You can see the status on your company settings page.
          <br />
          Please wait until the verification is completed otherwise you will get charged 25% VAT.
        </VATStatusText>
      )}
    </TabsContainer>
  );

  const renderRecurringTab = () => (
    <TabsContainer>
      <LabelHeading>To purchase a plan, please contact</LabelHeading>
      <EmailLink href="mailto:sales@futureskill.com">sales@futureskill.com</EmailLink>
    </TabsContainer>
  );

  return (
    <Wrapper minHeight="100vh">
      <Container usePadding="0" column full>
        <PlansContainer>
          <PlansWrapper>
            {loading && (
              <Spinner
                title="Please wait while your payment is processing"
                text="Do not refresh / reload the page until processing is done..."
              />
            )}
            {!loading && plan?._id && (
              <>
                <OptionsTab
                  handleTabs={(tab) => setIsPurchaseTab(tab === 'purchase' ? true : false)}
                  activeTab={isPurchaseTab ? 'purchase' : 'recurring'}
                  options={['recurring', 'purchase']}
                ></OptionsTab>
                {isPurchaseTab ? renderPurchaseTab() : renderRecurringTab()}
                <PaymentPageLink href={Path.payments}>Click here to see your previous purchases</PaymentPageLink>
              </>
            )}
          </PlansWrapper>
        </PlansContainer>
      </Container>
    </Wrapper>
  );
}
